import { BaseService } from "../baseService";
import { PalletAPIService } from "./palletAPIService";
import { FileAPIService } from "../fileAPIService";
import { sanitiseParams } from "src/utils";
import { PaginatedPallets, PalletPaginationOptions, PalletRecord, PopulatedPalletRecord } from "src/types";
import { PostPalletRequestBody } from "src/types/api/pallets/postPallet";
import { PatchPalletRequestBody } from "src/types/api/pallets/patchPallet";
import { GetPalletStatsResponse } from "src/types/api/pallets/getPalletStats";
import { PostPalletCompleteRequestBody } from "src/types/api/pallets/postPalletComplete";

class PalletService extends BaseService<PalletAPIService> {
  private readonly fileAPIService: FileAPIService;

  constructor(apiService: PalletAPIService, fileAPIService: FileAPIService) {
    super(apiService);
    this.fileAPIService = fileAPIService;
  }

  @sanitiseParams()
  async createPallet(requestBody: PostPalletRequestBody): Promise<PalletRecord> {
    return await this.apiService.postPallet(requestBody);
  }

  @sanitiseParams()
  async editPallet(palletId: string, requestBody: PatchPalletRequestBody): Promise<PalletRecord> {
    return await this.apiService.patchPallet(palletId, requestBody);
  }

  async getPallets(queryParams?: PalletPaginationOptions): Promise<PaginatedPallets> {
    return await this.apiService.getPallets(queryParams);
  }

  @sanitiseParams()
  async getPalletById(palletId: string): Promise<PopulatedPalletRecord> {
    return await this.apiService.getPalletById(palletId);
  }

  async getPalletStats(): Promise<GetPalletStatsResponse> {
    return await this.apiService.getPalletStats();
  }

  async getCompletePallets(queryParams?: PalletPaginationOptions): Promise<PaginatedPallets> {
    return await this.apiService.getCompletePallets(queryParams);
  }

  async getBuildingPallets(): Promise<PopulatedPalletRecord[]> {
    const { pallets } = await this.apiService.getBuildingPallets();
    return pallets;
  }

  @sanitiseParams()
  async completePallet(palletId: string, requestBody: PostPalletCompleteRequestBody): Promise<PalletRecord> {
    return await this.apiService.postPalletComplete(palletId, requestBody);
  }

  @sanitiseParams()
  async reopenPallet(palletId: string): Promise<PalletRecord> {
    return await this.apiService.postPalletReopen(palletId);
  }

  @sanitiseParams()
  async getPalletLabel(palletId: string): Promise<File> {
    const { url, fileName } = await this.apiService.getPalletLabelUrl(palletId);
    const fileBlob = await this.fileAPIService.getFile(url);
    const baseName = fileName || `pallet-label-${palletId}`;
    return new File([fileBlob], baseName.endsWith(".pdf") ? baseName : `${baseName}.pdf`, {
      type: "application/pdf",
      lastModified: new Date().getTime()
    });
  }

  async getPalletLabels(palletIds: string[]): Promise<File[]> {
    return await Promise.all(palletIds.map((palletId) => this.getPalletLabel(palletId)));
  }
}

export { PalletService };
